import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

const LandingPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const roles = [
    {
      id: 'student',
      icon: '🎓',
      title: language === 'tr' ? 'Öğrenci' : 'Student',
      description: language === 'tr' 
        ? 'TYT ve AP sınavlarına hazırlan, kişiselleştirilmiş öğrenme deneyimi yaşa'
        : 'Prepare for TYT and AP exams with personalized learning experience',
      features: language === 'tr'
        ? ['AI Mentor Desteği', 'Kişiselleştirilmiş Kitapçıklar', 'İlerleme Takibi']
        : ['AI Mentor Support', 'Personalized Booklets', 'Progress Tracking'],
      gradient: 'from-blue-500 to-cyan-500',
      hoverGradient: 'hover:from-blue-600 hover:to-cyan-600',
      path: '/dashboard'
    },
    {
      id: 'teacher',
      icon: '👨‍🏫',
      title: language === 'tr' ? 'Öğretmen' : 'Teacher',
      description: language === 'tr'
        ? 'AI ile soru üret, öğrenci ilerlemelerini takip et, içerik oluştur'
        : 'Generate questions with AI, track student progress, create content',
      features: language === 'tr'
        ? ['AI Soru Üretici', 'Soru Bankası Yönetimi', 'Öğrenci Raporları']
        : ['AI Question Generator', 'Question Bank Management', 'Student Reports'],
      gradient: 'from-purple-500 to-pink-500',
      hoverGradient: 'hover:from-purple-600 hover:to-pink-600',
      path: '/teacher/dashboard'
    },
    {
      id: 'parent',
      icon: '👨‍👩‍👧',
      title: language === 'tr' ? 'Ebeveyn' : 'Parent',
      description: language === 'tr'
        ? 'Çocuğunuzun eğitim sürecini takip edin, raporları görüntüleyin'
        : 'Monitor your child\'s educational progress and view reports',
      features: language === 'tr'
        ? ['Çocuk İlerlemesi', 'Performans Raporları', 'Haftalık Özet']
        : ['Child Progress', 'Performance Reports', 'Weekly Summary'],
      gradient: 'from-green-500 to-teal-500',
      hoverGradient: 'hover:from-green-600 hover:to-teal-600',
      path: '/parent/dashboard'
    },
    {
      id: 'admin',
      icon: '⚙️',
      title: language === 'tr' ? 'Admin' : 'Admin',
      description: language === 'tr'
        ? 'Soru onaylama, kullanıcı yönetimi, sistem ayarları'
        : 'Question approval, user management, system settings',
      features: language === 'tr'
        ? ['Soru Onayı', 'Kullanıcı Yönetimi', 'Sistem Ayarları']
        : ['Question Approval', 'User Management', 'System Settings'],
      gradient: 'from-orange-500 to-red-500',
      hoverGradient: 'hover:from-orange-600 hover:to-red-600',
      path: '/admin/dashboard'
    }
  ];

  const platformFeatures = [
    {
      icon: '🤖',
      title: language === 'tr' ? 'AI Destekli Öğrenme' : 'AI-Powered Learning',
      description: language === 'tr'
        ? 'Yapay zeka mentorleri ile birebir öğrenme deneyimi'
        : 'One-on-one learning experience with AI mentors'
    },
    {
      icon: '🌍',
      title: language === 'tr' ? 'Dual-Track Sistem' : 'Dual-Track System',
      description: language === 'tr'
        ? 'Hem TYT hem de AP sınavlarına aynı anda hazırlık'
        : 'Prepare for both TYT and AP exams simultaneously'
    },
    {
      icon: '📊',
      title: language === 'tr' ? 'Kişiselleştirilmiş İlerleme' : 'Personalized Progress',
      description: language === 'tr'
        ? 'Zayıf konularınıza odaklı, adaptif öğrenme yolu'
        : 'Adaptive learning path focused on your weak areas'
    },
    {
      icon: '🔄',
      title: language === 'tr' ? 'İki Dilli Terminoloji' : 'Bilingual Terminology',
      description: language === 'tr'
        ? 'Türkçe ve İngilizce bilimsel terimler arası köprü'
        : 'Bridge between Turkish and English scientific terms'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <LanguageToggle />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:py-24">
          {/* Logo & Brand */}
          <div className="text-center mb-12">
            <div className="flex justify-center items-center mb-6">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-4 rounded-2xl shadow-2xl">
                <span className="text-5xl">🎓</span>
              </div>
            </div>
            <h1 className="text-5xl sm:text-7xl font-extrabold mb-4">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                BridgePrep
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-2">
              {language === 'tr' 
                ? 'Global ve Yerel Sınavlar Arası Köprü'
                : 'Bridge Between Global and Local Exams'}
            </p>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-8">
              {language === 'tr'
                ? 'Hem uluslararası AP hem de Türkiye TYT/AYT sınavlarına yapay zeka destekli hazırlık platformu'
                : 'AI-powered preparation platform for both international AP and Turkish TYT/AYT exams'}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                {language === 'tr' ? 'Ücretsiz Başla' : 'Get Started Free'}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              
              <button
                onClick={() => navigate('/pricing')}
                className="inline-flex items-center bg-white text-gray-700 border-2 border-gray-300 px-8 py-4 rounded-xl text-lg font-semibold shadow-md hover:shadow-lg hover:border-purple-600 transform hover:scale-105 transition-all duration-300"
              >
                💎 {language === 'tr' ? 'Planlar & Fiyatlar' : 'Plans & Pricing'}
              </button>
            </div>
          </div>

          {/* Role Selection Cards */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
              {language === 'tr' ? '🚀 Platformu Keşfet' : '🚀 Explore Platform'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {roles.map((role) => (
                <div
                  key={role.id}
                  onClick={() => navigate(role.path)}
                  className={`group relative bg-white rounded-2xl shadow-xl overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl`}
                >
                  {/* Gradient Background on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${role.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <div className="relative p-8">
                    {/* Icon */}
                    <div className={`inline-flex p-4 bg-gradient-to-br ${role.gradient} rounded-2xl mb-6 text-white text-4xl shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
                      {role.icon}
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                      {role.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                      {role.description}
                    </p>
                    
                    {/* Features */}
                    <ul className="space-y-2 mb-6">
                      {role.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-700">
                          <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    {/* CTA Button */}
                    <div className={`flex items-center justify-center bg-gradient-to-r ${role.gradient} ${role.hoverGradient} text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all duration-300 group-hover:shadow-xl`}>
                      {language === 'tr' ? 'Giriş Yap' : 'Sign In'}
                      <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Platform Features Section */}
      <div className="relative py-20 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
            {language === 'tr' ? '✨ Platform Özellikleri' : '✨ Platform Features'}
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            {language === 'tr'
              ? 'BridgePrep, öğrencilere hem yerel hem de uluslararası sınavlarda başarılı olmak için ihtiyaç duydukları tüm araçları sunar'
              : 'BridgePrep provides students with all the tools they need to succeed in both local and international exams'}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {platformFeatures.map((feature, idx) => (
              <div key={idx} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl mb-4 text-3xl shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="relative py-16 bg-gradient-to-br from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">331+</div>
              <div className="text-blue-100">{language === 'tr' ? 'Soru Bankası' : 'Question Bank'}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">82</div>
              <div className="text-blue-100">{language === 'tr' ? 'AI Dual-Track Soru' : 'AI Dual-Track Questions'}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">5</div>
              <div className="text-blue-100">{language === 'tr' ? 'Disiplin' : 'Disciplines'}</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">2</div>
              <div className="text-blue-100">{language === 'tr' ? 'Dil Desteği' : 'Language Support'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* User Guide Section */}
      <div className="relative py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              📖 {language === 'tr' ? 'Kullanım Kılavuzu' : 'User Guide'}
            </h2>
            <p className="text-xl text-gray-600">
              {language === 'tr' 
                ? 'BridgePrep\'i nasıl kullanacağınızı öğrenin'
                : 'Learn how to use BridgePrep'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-200">
              <div className="text-4xl mb-4">1️⃣</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                {language === 'tr' ? 'Kayıt Ol' : 'Sign Up'}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {language === 'tr'
                  ? 'Rolünüzü seçin (Öğrenci, Öğretmen, Ebeveyn) ve Google ile giriş yapın'
                  : 'Choose your role (Student, Teacher, Parent) and sign in with Google'}
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• {language === 'tr' ? 'Ücretsiz kayıt' : 'Free registration'}</li>
                <li>• {language === 'tr' ? 'Google OAuth güvenli' : 'Google OAuth secure'}</li>
                <li>• {language === 'tr' ? 'Anında başla' : 'Instant access'}</li>
              </ul>
            </div>

            {/* Step 2 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200">
              <div className="text-4xl mb-4">2️⃣</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                {language === 'tr' ? 'Mod Seç' : 'Choose Mode'}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {language === 'tr'
                  ? 'TYT veya AP sınav modunu seçin'
                  : 'Select TYT or AP exam mode'}
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• {language === 'tr' ? 'TYT: Hız odaklı' : 'TYT: Speed focused'}</li>
                <li>• {language === 'tr' ? 'AP: Kavramsal' : 'AP: Conceptual'}</li>
                <li>• {language === 'tr' ? 'Kolayca geçiş' : 'Easy switching'}</li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-6 border-2 border-green-200">
              <div className="text-4xl mb-4">3️⃣</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                {language === 'tr' ? 'Öğren' : 'Learn'}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {language === 'tr'
                  ? 'AI mentor, soru bankası ve kişisel kitapçıklar'
                  : 'AI mentor, question bank, and personalized booklets'}
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• {language === 'tr' ? 'AI yardımcı' : 'AI assistant'}</li>
                <li>• {language === 'tr' ? 'Soru çöz' : 'Solve questions'}</li>
                <li>• {language === 'tr' ? 'İlerleme takip' : 'Track progress'}</li>
              </ul>
            </div>

            {/* Step 4 */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 border-2 border-orange-200">
              <div className="text-4xl mb-4">4️⃣</div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                {language === 'tr' ? 'Başarılı Ol' : 'Succeed'}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {language === 'tr'
                  ? 'Kişiselleştirilmiş raporlar ve yeterlilik analizi'
                  : 'Personalized reports and proficiency analysis'}
              </p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• {language === 'tr' ? 'Zayıf konular' : 'Weak topics'}</li>
                <li>• {language === 'tr' ? 'Gelişim grafiği' : 'Growth chart'}</li>
                <li>• {language === 'tr' ? 'Sınav hazırlığı' : 'Exam ready'}</li>
              </ul>
            </div>
          </div>

          {/* Beta Notice */}
          <div className="mt-12 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-2xl p-6">
            <div className="flex items-start">
              <div className="text-4xl mr-4">🚀</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {language === 'tr' ? 'Beta Sürümü' : 'Beta Version'}
                </h3>
                <p className="text-gray-700 mb-4">
                  {language === 'tr'
                    ? 'BridgePrep şu anda beta aşamasında. Geri bildirimleriniz bizim için çok değerli!'
                    : 'BridgePrep is currently in beta. Your feedback is invaluable to us!'}
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="#feedback"
                    className="inline-flex items-center bg-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-orange-600 transition"
                  >
                    📝 {language === 'tr' ? 'Geri Bildirim Gönder' : 'Send Feedback'}
                  </a>
                  <a
                    href="https://github.com/bridgeprep"
                    className="inline-flex items-center bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-800 transition"
                  >
                    💻 GitHub
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Section */}
      <div id="feedback" className="relative py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              💬 {language === 'tr' ? 'Geri Bildirim' : 'Feedback'}
            </h2>
            <p className="text-xl text-gray-600">
              {language === 'tr'
                ? 'Düşüncelerinizi bizimle paylaşın'
                : 'Share your thoughts with us'}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Geri Bildirim Tipi' : 'Feedback Type'}
                  </label>
                  <select className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="bug">🐛 {language === 'tr' ? 'Hata Bildirimi' : 'Bug Report'}</option>
                    <option value="feature">✨ {language === 'tr' ? 'Özellik Önerisi' : 'Feature Request'}</option>
                    <option value="improvement">🔧 {language === 'tr' ? 'İyileştirme' : 'Improvement'}</option>
                    <option value="question">❓ {language === 'tr' ? 'Soru' : 'Question'}</option>
                    <option value="other">💭 {language === 'tr' ? 'Diğer' : 'Other'}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'tr' ? 'Öncelik' : 'Priority'}
                  </label>
                  <select className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="low">🟢 {language === 'tr' ? 'Düşük' : 'Low'}</option>
                    <option value="medium">🟡 {language === 'tr' ? 'Orta' : 'Medium'}</option>
                    <option value="high">🔴 {language === 'tr' ? 'Yüksek' : 'High'}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Başlık' : 'Title'}
                </label>
                <input
                  type="text"
                  placeholder={language === 'tr' ? 'Kısa bir başlık...' : 'Short title...'}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Açıklama' : 'Description'}
                </label>
                <textarea
                  rows={6}
                  placeholder={language === 'tr' ? 'Detaylı açıklama...' : 'Detailed description...'}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  ℹ️ {language === 'tr'
                    ? 'Geri bildiriminizi göndermek için giriş yapmanız gerekmektedir. Lütfen önce bir hesap oluşturun.'
                    : 'You need to be logged in to submit feedback. Please create an account first.'}
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                {language === 'tr' ? 'Giriş Yap ve Gönder' : 'Login and Submit'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Pricing Preview */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {language === 'tr' ? '💳 Esnek Fiyatlandırma' : '💳 Flexible Pricing'}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {language === 'tr' 
              ? 'İhtiyacınıza uygun planı seçin. İlk 30 gün ücretsiz!' 
              : 'Choose the plan that fits your needs. First 30 days free!'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'tr' ? 'Ücretsiz Deneme' : 'Free Trial'}
            </h3>
            <div className="text-4xl font-bold text-blue-600 mb-4">₺0</div>
            <p className="text-gray-600 mb-6">{language === 'tr' ? '30 gün' : '30 days'}</p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✓ {language === 'tr' ? '5 AI soru/gün' : '5 AI questions/day'}</li>
              <li>✓ {language === 'tr' ? '10 mentor mesaj/gün' : '10 mentor messages/day'}</li>
              <li>✓ {language === 'tr' ? '2 kitapçık' : '2 booklets'}</li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-2xl shadow-2xl p-8 text-center transform scale-105">
            <div className="bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full inline-block mb-2">
              {language === 'tr' ? '🔥 EN POPÜLER' : '🔥 MOST POPULAR'}
            </div>
            <h3 className="text-2xl font-bold mb-2">
              {language === 'tr' ? 'Öğrenci' : 'Student'}
            </h3>
            <div className="text-4xl font-bold mb-4">₺99</div>
            <p className="opacity-90 mb-6">{language === 'tr' ? 'aylık' : 'per month'}</p>
            <ul className="space-y-2 text-sm">
              <li>✓ {language === 'tr' ? '50 AI soru/gün' : '50 AI questions/day'}</li>
              <li>✓ {language === 'tr' ? '100 mentor mesaj/gün' : '100 mentor messages/day'}</li>
              <li>✓ {language === 'tr' ? '20 kitapçık + analiz' : '20 booklets + analytics'}</li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'tr' ? 'Öğretmen' : 'Teacher'}
            </h3>
            <div className="text-4xl font-bold text-purple-600 mb-4">₺149</div>
            <p className="text-gray-600 mb-6">{language === 'tr' ? 'aylık' : 'per month'}</p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✓ {language === 'tr' ? '100 AI soru/gün' : '100 AI questions/day'}</li>
              <li>✓ {language === 'tr' ? '200 mentor mesaj/gün' : '200 mentor messages/day'}</li>
              <li>✓ {language === 'tr' ? '50 kitapçık + raporlar' : '50 booklets + reports'}</li>
            </ul>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate('/pricing')}
            className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all"
          >
            {language === 'tr' ? '📊 Tüm Planları Gör' : '📊 View All Plans'}
          </button>
        </div>
      </div>

      {/* Institutional Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                {language === 'tr' ? '🏫 Okullar ve Kurslar' : '🏫 Schools & Institutions'}
              </h2>
              <p className="text-xl mb-6 opacity-90">
                {language === 'tr'
                  ? 'Kurumunuz için özel çözümler ve fiyatlandırma'
                  : 'Custom solutions and pricing for your institution'}
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <span className="mr-3">✓</span>
                  {language === 'tr' ? 'Öğrenci başına ₺80/ay' : '₺80/student/month'}
                </li>
                <li className="flex items-center">
                  <span className="mr-3">✓</span>
                  {language === 'tr' ? 'Toplu öğrenci yönetimi' : 'Bulk student management'}
                </li>
                <li className="flex items-center">
                  <span className="mr-3">✓</span>
                  {language === 'tr' ? 'Detaylı raporlama' : 'Detailed reporting'}
                </li>
                <li className="flex items-center">
                  <span className="mr-3">✓</span>
                  {language === 'tr' ? 'Özel içerik' : 'Custom content'}
                </li>
              </ul>
              <button
                onClick={() => navigate('/institution-demo')}
                className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all"
              >
                {language === 'tr' ? '📞 Demo Talep Et' : '📞 Request Demo'}
              </button>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8">
              <div className="text-white text-center">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-2xl font-bold mb-4">
                  {language === 'tr' ? 'Örnek Hesaplama' : 'Example Calculation'}
                </h3>
                <div className="bg-white/20 rounded-xl p-6">
                  <p className="text-xl mb-2">100 {language === 'tr' ? 'öğrenci' : 'students'}</p>
                  <p className="text-4xl font-bold mb-2">₺8,000</p>
                  <p className="opacity-90">{language === 'tr' ? 'aylık' : 'per month'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-20">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'tr' 
              ? 'Hemen Başla ve Farkı Gör'
              : 'Start Now and See the Difference'}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {language === 'tr'
              ? 'Ücretsiz hesap oluşturun ve 30 gün boyunca tüm özellikleri deneyin'
              : 'Create a free account and try all features for 30 days'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300"
            >
              {language === 'tr' ? 'Ücretsiz Başla' : 'Start Free Trial'}
              <svg className="w-6 h-6 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
            >
              {language === 'tr' ? 'Fiyatları İncele' : 'View Pricing'}
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm">
            © 2024 BridgePrep. {language === 'tr' ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            {language === 'tr' 
              ? 'Global ve yerel sınavlar arası köprü'
              : 'Bridge between global and local exams'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
